import { Middleware } from "@reduxjs/toolkit";

import type { RootState } from "@src/store/rootState";

import {
  clearDashboardFilters,
  setAnalyticsSubpage,
  setCompareGroups,
  setDashboardFilter,
  setDashboardFilterByExperience,
  updateDashboardFilter,
} from "../slices/dashboardFilters";
import { IExperienceDashboardFilters } from "../types/dashboardFilters";

import {
  serializeFilterValue,
  SYNCABLE_FILTERS,
} from "./dashboardFiltersUrlHelpers";

const updateUrlFromFilters = (
  filters: Partial<IExperienceDashboardFilters>
) => {
  const url = new URL(window.location.href);
  const params = url.searchParams;

  SYNCABLE_FILTERS.forEach((key) => {
    params.delete(key);
  });

  params.delete("subpage");

  SYNCABLE_FILTERS.forEach((key) => {
    const value = filters[key];
    const serialized = serializeFilterValue(value, key);
    if (serialized !== null) {
      params.set(key, serialized);
    }
  });

  window.history.replaceState(null, "", url.toString());
};

const isAnalyticsPage = (): boolean => {
  const path = window.location.pathname;
  return /^\/(personalization|experiment)\/[^/]+/.test(path);
};

// Double assertion bypasses expensive RootState middleware type check (saves ~690ms in tsc)
export const dashboardFiltersUrlSyncMiddleware = ((store) =>
  (next) =>
  (action) => {
    const result = next(action);

    if (!isAnalyticsPage()) {
      return result;
    }

    const filterActions: string[] = [
      updateDashboardFilter.type,
      setDashboardFilter.type,
      clearDashboardFilters.type,
      setDashboardFilterByExperience.type,
      setCompareGroups.type,
      setAnalyticsSubpage.type,
    ];

    if (typeof action === "object" && action !== null && "type" in action) {
      const actionType = (action as { type: string }).type;
      if (filterActions.includes(actionType)) {
        const state = store.getState();
        const filters = state.dashboardFilters;

        updateUrlFromFilters(filters);
      }
    }

    return result;
  }) as Middleware<object, RootState>;
